/**
 * Banner slider for sections/shugar-banner-slider.liquid.
 *
 * Slides sit in a native scroll-snap track, so swiping, trackpads and
 * keyboard scrolling work without JavaScript. This element only adds the
 * arrows, the dots, optional autoplay, and theme-editor block selection.
 */
class ShugarBannerSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement[]} */
  #dots = [];
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {number | null} */
  #timer = null;
  /** @type {IntersectionObserver | null} */
  #visibility = null;
  #current = 0;
  #paused = false;
  #offscreen = false;

  connectedCallback() {
    this.#track = this.querySelector('.shugar-slider__track');
    this.#slides = [...this.querySelectorAll('.shugar-slider__slide')];
    this.#dots = [...this.querySelectorAll('.shugar-slider__dot')];
    if (!this.#track || this.#slides.length < 2) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    this.addEventListener('touchstart', this.#pause, { passive: true });
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#resume);

    // Keep the dots in sync however the slide changed (swipe, arrows, autoplay).
    this.#observer = new IntersectionObserver(this.#onIntersect, { root: this.#track, threshold: 0.6 });
    this.#slides.forEach((slide) => this.#observer?.observe(slide));

    // A clip playing below the fold is wasted bandwidth and battery.
    this.#visibility = new IntersectionObserver(this.#onVisible, { threshold: 0 });
    this.#visibility.observe(this);

    for (const video of this.#videos()) {
      video.addEventListener('ended', this.#onVideoEnded);
    }

    this.#syncVideos();
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    this.#visibility?.disconnect();
    for (const video of this.#videos()) {
      video.removeEventListener('ended', this.#onVideoEnded);
    }
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
    this.removeEventListener('touchstart', this.#pause);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#resume);
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    if (!this.#track) return;
    const count = this.#slides.length;
    const target = ((index % count) + count) % count;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.scrollTo({ left: target * this.#track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setCurrent(target);
  }

  /**
   * @param {number} index
   */
  #setCurrent(index) {
    this.#current = index;
    this.#dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
    this.#syncVideos();
  }

  /** @returns {HTMLVideoElement[]} */
  #videos() {
    return [...this.querySelectorAll('video')];
  }

  /**
   * Only the slide on screen plays. Everything else is paused and rewound, so a
   * clip always starts from the top when the shopper comes back to it.
   */
  #syncVideos() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      if (index !== this.#current || this.#offscreen) {
        video.pause();
        video.currentTime = 0;
        return;
      }

      if (reduceMotion || !video.autoplay) return;
      // Muted is what makes autoplay allowed; Safari also needs it set in JS.
      video.muted = true;
      video.play().catch(() => {
        // Autoplay refused — the poster stays up, which is a fine fallback.
      });
    });
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const control = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-slide-step], [data-slide-to]');
    if (!(control instanceof HTMLElement)) return;

    if (control.dataset.slideStep) {
      this.goTo(this.#current + Number(control.dataset.slideStep));
    } else {
      this.goTo(Number(control.dataset.slideTo));
    }
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#setCurrent(this.#slides.indexOf(/** @type {HTMLElement} */ (entry.target)));
      }
    }
  };

  /**
   * In the theme editor, selecting a slide block scrolls to it and holds it there.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const block = /** @type {HTMLElement | null} */ (event.target);
    const index = this.#slides.findIndex((slide) => slide === block || slide.contains(block));
    if (index === -1) return;
    this.#pause();
    this.goTo(index);
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onVisible = (entries) => {
    for (const entry of entries) {
      this.#offscreen = !entry.isIntersecting;
    }
    this.#syncVideos();
  };

  /** A clip set not to loop hands the slider on once it finishes. */
  #onVideoEnded = (event) => {
    const slide = /** @type {HTMLElement | null} */ (event.target)?.closest('.shugar-slider__slide');
    if (!slide || slide.dataset.holdUntilEnd !== 'true') return;
    if (this.#slides.indexOf(slide) !== this.#current) return;
    if (this.#paused) return;

    this.goTo(this.#current + 1);
  };

  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
    } else if (!this.#paused) {
      this.#start();
    }
  };

  #pause = () => {
    this.#paused = true;
    this.#stop();
  };

  #resume = () => {
    this.#paused = false;
    this.#start();
  };

  #start() {
    this.#stop();
    if (this.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const interval = Math.max(2000, Number(this.dataset.interval) || 5000);
    this.#timer = window.setInterval(() => {
      // A slide waiting on its clip advances from the video's 'ended' event instead.
      if (this.#slides[this.#current]?.dataset.holdUntilEnd === 'true') return;
      this.goTo(this.#current + 1);
    }, interval);
  }

  #stop() {
    if (this.#timer !== null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('shugar-banner-slider')) {
  customElements.define('shugar-banner-slider', ShugarBannerSlider);
}
